import { useParams, useNavigate } from '@tanstack/react-router';
import ConfirmButtons from '@zeus/core/buttons/ConfirmButtons';
import ErrorField from '@zeus/core/ErrorField';
import { BreadcrumbItem } from '@zeus/core/layout/MainLayout';
import PageTitle from '@zeus/core/layout/PageTitle';
import RequiredField from '@zeus/core/RequiredField';
import SlugField from '@zeus/core/SlugField';
import MarkdownEditor, { README_EDITOR_OPTIONS } from '_/common/components/MarkdownEditor';
import { PROJECT_FLOW_TYPES, DEFAULT_README } from '_/constants/common';
import { useFlowContext } from '_/contexts/FlowContext';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { EFlowType, IFlow, IFlowDefinitionChain, ISourceCriteria } from '_/features/flows/common/types/flow.types';
import { cleanupSourceCriteria } from '_/features/flows/common/utils/flowUtils';
import { useCreateFlow, useUpdateFlow, useSaveFlowReadme } from '_/hooks/useFlows';
import useOrgAccess from '_/hooks/useOrgAccess';
import { ICollection } from '_/types/collections/collections.types';
import createSuggestedSlug from '_/utils/createSuggestedSlug';
import {
  filterErrorsForField,
  checkPositiveNumberWithMinMaxValue,
  checkNumberValue,
  validateSlug,
  checkIfSlugExists,
} from '_/utils/formValidations';
import { SOURCE_ENGINEER_NOT_INCLUDED } from '_/utils/userManagement';
import { checkIfNameExists } from '_/utils/validation/nameValidator';
import { Modal, Checkbox, Divider, Alert, Input, InputNumber, Select, Form, Space } from 'antd';
import { isValidCron } from 'cron-validator';
import { isEqual, cloneDeep, pickBy } from 'lodash';
import React, { useCallback, useEffect, useState, ReactElement, useRef, useMemo } from 'react';

import FlowCronExpression from './FlowCronExpression';
import LegacyExtractorFlow from './FlowTypes/LegacyExtractorFlow';
import SimpleExtractorFlow from './FlowTypes/SimpleExtractorFlow';
import classes from './NewProjectFlow.module.css';

const flowTypeOptions = PROJECT_FLOW_TYPES.map((type) => ({
  label: type,
  value: type,
  key: type,
}));

interface EncryptedConfig {
  accessKeyId: string;
  secretAccessKey: string;
}

interface InputUriTemplate {
  bucket: string;
  path: string;
  fileName: string;
  extension?: string;
}

// Reuse ISourceCriteria from flow.types.ts instead of redefining
export interface FlowValues {
  name: string;
  cron: string[];
  pushHours?: number;
  dataHours?: number;
  closeHours?: number;
  active: boolean;
  definition: string;
  type: EFlowType;
  inputUriTemplate: InputUriTemplate;
  shuffleOnInputUriChange?: boolean;
  collectionId: string;
  cronScheduling: boolean;
  s3Configuration: boolean;
  slug: string;
  chunks?: number;
  chunkCollectHours?: number;
  chunkPushHours?: number;
  chunkTimeoutHours?: number;
  sourceFiltering: boolean;
  sourceCriteria: ISourceCriteria | null;
  chains: IFlowDefinitionChain[];
  chainingParameter?: string;
  aggregateChunks?: boolean;
  retainOriginalChunkFiles?: boolean;
  ignoreAggregationDupes?: boolean;
  skipQaTests: boolean;
  inputTransform?: string;
}

interface NewProjectFlowProps {
  collections: ICollection[];
  flows: IFlow[];
}

function NewProjectFlow({ collections, flows }: NewProjectFlowProps): ReactElement {
  const [form] = Form.useForm();
  const history = useNavigate();
  const params = useParams({ strict: false }) as Partial<{ flowSlug?: string }>;
  const flowSlug = params.flowSlug || '';
  const access = useOrgAccess(SOURCE_ENGINEER_NOT_INCLUDED);
  const { setBreadcrumbs } = useTopHeader();

  const { selectedOrganization } = useOrganization();
  const selectedOrgSlug = selectedOrganization?.slug;
  const { selectedProject, selectedProjectSlug } = useProject();
  const projectId = selectedProject?.id;

  // Use FlowContext to get selected flow data
  const { selectedFlow, flowId } = useFlowContext();
  const { readmeContent } = selectedFlow || {};

  // React Query mutations
  const { mutateAsync: createFlowAsync, isPending: isCreating, error: createError } = useCreateFlow(projectId);
  const { mutateAsync: updateFlowAsync, isPending: isUpdating, error: updateError } = useUpdateFlow(projectId, flowSlug, flowId);
  const { mutateAsync: saveFlowReadmeAsync } = useSaveFlowReadme(projectId, flowSlug, !!selectedFlow?.readme);

  const isSavingFlow = isCreating || isUpdating;

  // Extract error messages from mutation errors - memoized to prevent infinite loops
  const saveFlowErrors = useMemo(() => {
    return createError || updateError
      ? [
          {
            type: 'api',
            error: (createError as any)?.message || (updateError as any)?.message || 'Error saving flow',
          },
        ]
      : [];
  }, [createError, updateError]);

  // Use ref to track if we've initialized the form to prevent multiple calls to setInitialValues
  const hasInitialized = useRef(false);

  const [isEditMode, setEditMode] = useState(false);
  const [slugIsDirty, setSlugIsDirty] = useState(false);
  const [flowSlugErrors, setFlowSlugErrors] = useState<string[]>([]);
  const [flowNameErrors, setFlowNameErrors] = useState<string[]>([]);
  const [flowApiErrors, setFlowApiErrors] = useState<string[]>([]);
  const [isWarningModalVisible, setWarningModalVisible] = useState(false);
  const [encryptedConfig, setEncryptedConfig] = useState<EncryptedConfig>({
    accessKeyId: '',
    secretAccessKey: '',
  });

  // Local state for form values - using plain React state instead of Ant Design Form
  const [values, setValues] = useState<FlowValues>({
    name: '',
    cron: ['', '', '', '', ''],
    pushHours: undefined,
    dataHours: undefined,
    closeHours: undefined,
    active: true,
    definition: '',
    type: EFlowType.SIMPLE,
    inputUriTemplate: {
      bucket: '',
      path: '',
      fileName: '',
      extension: '.json',
    },
    shuffleOnInputUriChange: false,
    collectionId: '',
    cronScheduling: false,
    s3Configuration: false,
    slug: '',
    chunks: undefined,
    chunkCollectHours: undefined,
    chunkPushHours: undefined,
    chunkTimeoutHours: undefined,
    sourceFiltering: false,
    sourceCriteria: null,
    chains: [{ collectionId: '' }],
    aggregateChunks: undefined,
    retainOriginalChunkFiles: undefined,
    ignoreAggregationDupes: undefined,
    skipQaTests: false,
    inputTransform: '',
  });

  // Local state for form tracking
  const [flowReadme, setFlowReadme] = useState('');
  const [isFormDirty, setFormDirty] = useState(false);
  const [isValidSlug, setValidSlug] = useState(true);
  const [_isValidCronExpression, _setValidCron] = useState(true);
  const [invalidChainArray, setInvalidChainArray] = useState<number[]>([]);
  const [, _setIsChainedDisplayDirty] = useState(false);
  const [s3ConfiguredBucket, setS3ConfiguredBucket] = useState('');

  // Form handlers
  const handleFormDirtyCheck = useCallback((originalValue: any, newValue: any) => {
    if (!isEqual(originalValue, newValue)) {
      setFormDirty(true);
    }
  }, []);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value, checked, type } = event.target;
      const fieldValue = type === 'checkbox' ? checked : value;

      setValues((prev) => ({ ...prev, [name]: fieldValue }));
      form.setFieldsValue({ [name]: fieldValue });

      // Handle validations
      if (name === 'name' && !slugIsDirty) {
        const suggestedSlug = createSuggestedSlug(value);
        setValues((prev) => ({ ...prev, slug: suggestedSlug }));
        form.setFieldsValue({ slug: suggestedSlug });
      }

      if (name === 'slug') {
        setValidSlug(validateSlug(value));
        setSlugIsDirty(true);
        const duplicateSlug = checkIfSlugExists(flows, value);
        if (duplicateSlug) {
          setFlowSlugErrors([`A flow with the slug "${value}" already exists`]);
        } else {
          setFlowSlugErrors([]);
        }
      }

      if (name === 'name') {
        if (isEditMode && selectedFlow && value.toLowerCase() === selectedFlow.name.toLowerCase()) {
          setFlowNameErrors([]);
        } else {
          const duplicateName = checkIfNameExists(flows, value);
          if (duplicateName) {
            setFlowNameErrors([`A flow with the name "${value}" already exists`]);
          } else {
            setFlowNameErrors([]);
          }
        }
      }

      setFormDirty(true);
    },
    [form, slugIsDirty, flows, isEditMode, selectedFlow],
  );

  const handleTypeChange = useCallback(
    (value: string) => {
      if (value === 'LEGACY') {
        setValues((prev) => ({ ...prev, type: value as any }));
        form.setFieldsValue({ type: value });
      } else {
        setS3ConfiguredBucket('');
        setValues((prev) => ({
          ...prev,
          type: value as any,
          inputUriTemplate: {
            bucket: '',
            path: '',
            fileName: '',
            extension: '.json',
          },
          chunks: undefined,
          chunkCollectHours: undefined,
        }));
        form.setFieldsValue({ type: value });
      }

      setFormDirty(values.type !== value ? false : isFormDirty);
    },
    [form, values.type, isFormDirty],
  );

  const handleCronChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newCron = [...values.cron];
      const index = event.target.name as any;
      newCron[index] = event.target.value;

      setValues((prev) => ({ ...prev, cron: newCron }));

      // Sync with form fields
      const cronFieldNames = ['cronMin', 'cronHours', 'cronDayMonth', 'cronMonth', 'cronDayWeek'];
      form.setFieldsValue({
        [cronFieldNames[index]]: event.target.value,
      });

      const validCron = isValidCron(newCron.join(' '), { alias: true });
      _setValidCron(validCron);
      setFormDirty(true);
    },
    [values.cron, form],
  );

  const setS3Bucket = useCallback((templateInfo: InputUriTemplate) => {
    const bucket = templateInfo?.bucket ? `${templateInfo.bucket}` : '';
    const path = templateInfo?.path ? `/${templateInfo.path}` : '';
    const fileName = templateInfo?.fileName ? `${templateInfo.fileName}` : '';
    const slash = templateInfo?.path?.length && templateInfo?.fileName?.length ? '/' : '';
    const extension = templateInfo?.bucket && templateInfo?.extension ? templateInfo.extension || '.json' : '';

    const s3Url = templateInfo?.bucket ? `s3://${bucket}${path}${slash}${fileName}${extension}` : '';
    setS3ConfiguredBucket(s3Url);
  }, []);

  const handleS3Change = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // Get current form values to ensure we have all S3 fields
      const formValues = form.getFieldsValue(['bucket', 'path', 'fileName', 'extension']);

      const updatedTemplate = {
        ...values.inputUriTemplate,
        ...formValues,
        [event.target.name]: event.target.value,
      };

      setValues((prev) => ({ ...prev, inputUriTemplate: updatedTemplate }));

      // Also update form field to keep form state in sync
      form.setFieldsValue({
        [event.target.name]: event.target.value,
      });

      setS3Bucket(updatedTemplate);
      setFormDirty(true);
    },
    [values.inputUriTemplate, form, setS3Bucket],
  );

  const addChainToFlowArray = useCallback(() => {
    const initialChain = { collectionId: '', transform: '' };
    const newChains = values.chains.length === 0 ? [{ collectionId: '' }] : [...values.chains, initialChain];
    const newIdx = newChains.length - 1;

    setValues((prev) => ({ ...prev, chains: newChains }));

    // Initialize form fields for the new chain
    if (newIdx > 0) {
      form.setFieldsValue({
        [`chain-${newIdx}-transform`]: '',
        [`chain-${newIdx}-keyTransform`]: '',
      });
    }
    form.setFieldsValue({
      [`chain-${newIdx}-collectionId`]: '',
      [`chain-${newIdx}-dataHours`]: undefined,
      [`chain-${newIdx}-chunkCollectHours`]: undefined,
    });

    _setIsChainedDisplayDirty(false);
  }, [values.chains, form]);

  const validateChains = useCallback((chains: IFlowDefinitionChain[]) => {
    const validChain: number[] = [];

    chains.forEach((chain, idx) => {
      if (idx === 0 && !chain.collectionId) {
        validChain.push(idx);
      } else if (idx !== 0 && (!chain.keyTransform || !chain.collectionId)) {
        validChain.push(idx);
      }
    });

    setInvalidChainArray(validChain);
  }, []);

  const handleChainChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, idx: number) => {
      const propName = event.target.name;
      const value = event.target.value;

      const updatedChains = [...values.chains];
      updatedChains[idx] = { ...updatedChains[idx], [propName]: value };

      setValues((prev) => {
        const newValues = { ...prev, chains: updatedChains };
        if (updatedChains.length > 0 && (!prev.collectionId || prev.collectionId !== updatedChains[0].collectionId)) {
          newValues.collectionId = updatedChains[0].collectionId;
        }
        return newValues;
      });

      _setIsChainedDisplayDirty(true);
      validateChains(updatedChains);
      setFormDirty(true);
    },
    [values.chains, validateChains],
  );

  const handleChainCancel = useCallback(
    (_event: React.MouseEvent, idx: number) => {
      const updatedChains = [...values.chains];
      updatedChains.splice(idx, 1);

      setValues((prev) => ({ ...prev, chains: updatedChains }));
      setFormDirty(true);
      _setIsChainedDisplayDirty(true);
    },
    [values.chains],
  );

  const handleSubmit = async () => {
    try {
      // Trigger form validation
      await form.validateFields();

      // If validation passes, check for warning modal
      if (isEditMode && currentValues.type !== 'LEGACY') {
        const sourceCriteria = currentValues.sourceFiltering ? cleanupSourceCriteria(currentValues.sourceCriteria) : null;
        if ((currentValues.sourceFiltering && !sourceCriteria) || (selectedFlow?.sourceCriteria && !sourceCriteria)) {
          toggleWarningModal();
          return;
        }
      }

      // Save the flow
      await saveFlow();
    } catch {
      // Validation failed - error is shown in form
    }
  };

  useEffect(() => {
    setFlowApiErrors(filterErrorsForField(saveFlowErrors, 'api'));
    setFlowNameErrors(filterErrorsForField(saveFlowErrors, 'name'));
    setFlowSlugErrors(filterErrorsForField(saveFlowErrors, 'slug'));
  }, [saveFlowErrors]);

  useEffect(() => {
    if (flowSlug && selectedFlow && selectedFlow.id && !hasInitialized.current) {
      // Only initialize once
      hasInitialized.current = true;

      let flowData: FlowValues;
      if (selectedFlow.type === 'SIMPLE' || selectedFlow.type === 'CHAINED') {
        const template = setupInputUriTemplateSections(selectedFlow.definition?.inputUriTemplate);
        flowData = {
          ...selectedFlow,
          inputUriTemplate: selectedFlow.definition?.inputUriTemplate ? template : { bucket: '', path: '', fileName: '' },
          chunks: selectedFlow.definition?.chunks >= 0 ? selectedFlow.definition.chunks : undefined,
          chunkCollectHours:
            selectedFlow.definition?.chunkCollectHours >= 0 ? selectedFlow.definition.chunkCollectHours : undefined,
          chunkPushHours: selectedFlow.definition?.chunkPushHours >= 0 ? selectedFlow.definition.chunkPushHours : undefined,
          chunkTimeoutHours:
            selectedFlow.definition?.chunkTimeoutHours >= 0 ? selectedFlow.definition.chunkTimeoutHours : undefined,
          s3Configuration: !!selectedFlow.definition?.inputUriTemplate,
          cron: selectedFlow.cron ? selectedFlow.cron?.split(' ') : ['', '', '', '', ''],
          collectionId: selectedFlow.definition.collectionId,
          cronScheduling: !!selectedFlow.cron,
          sourceFiltering: !!selectedFlow.sourceCriteria,
          sourceCriteria: selectedFlow.sourceCriteria,
          chains: selectedFlow.definition?.chains?.length >= 0 ? cloneDeep(selectedFlow.definition.chains) : undefined,
          chainingParameter: selectedFlow.definition?.chainingParameter ? selectedFlow.definition.chainingParameter : undefined,
          aggregateChunks: selectedFlow.definition?.aggregateChunks,
          retainOriginalChunkFiles: selectedFlow.definition?.retainOriginalChunkFiles,
          ignoreAggregationDupes: selectedFlow.definition?.ignoreAggregationDupes,
          inputTransform: selectedFlow.definition?.inputTransform,
        } as FlowValues;
        setS3Bucket(template);
      } else {
        flowData = {
          ...selectedFlow,
          cron: selectedFlow.cron ? selectedFlow.cron?.split(' ') : ['', '', '', '', ''],
          collectionId: selectedFlow.definition.collectionId,
          cronScheduling: !!selectedFlow.cron,
          sourceFiltering: false, // this should always be false for legacy flow
        } as FlowValues;
      }

      // Set form values using state and antd form
      setValues(flowData);

      // Build source filter form fields
      const sourceFilterFields: any = {};
      if (flowData.sourceCriteria?.filters) {
        flowData.sourceCriteria.filters.forEach((filter: any, idx: number) => {
          if (filter.parameters) {
            Object.entries(filter.parameters).forEach(([param, value]) => {
              sourceFilterFields[`filter-${idx}-${param}`] = value;
            });
          }
        });
      }

      // Build chain form fields
      const chainFields: any = {};
      if (flowData.chains?.length) {
        flowData.chains.forEach((chain: any, idx: number) => {
          if (idx > 0) {
            chainFields[`chain-${idx}-transform`] = chain.transform || '';
            chainFields[`chain-${idx}-keyTransform`] = chain.keyTransform || '';
          }
          chainFields[`chain-${idx}-collectionId`] = chain.collectionId || '';
          chainFields[`chain-${idx}-dataHours`] = chain.dataHours;
          chainFields[`chain-${idx}-chunkCollectHours`] = chain.chunkCollectHours;
        });
      }

      form.setFieldsValue({
        name: flowData.name,
        slug: flowData.slug,
        type: flowData.type,
        active: flowData.active,
        collectionId: flowData.collectionId,
        dataHours: flowData.dataHours,
        pushHours: flowData.pushHours,
        closeHours: flowData.closeHours,
        cronScheduling: flowData.cronScheduling,
        s3Configuration: flowData.s3Configuration,
        skipQaTests: flowData.skipQaTests,
        sourceFiltering: flowData.sourceFiltering,
        // S3 fields
        bucket: flowData.inputUriTemplate?.bucket,
        path: flowData.inputUriTemplate?.path,
        fileName: flowData.inputUriTemplate?.fileName,
        extension: flowData.inputUriTemplate?.extension,
        // Transform fields
        inputTransform: flowData.inputTransform,
        chainingParameter: flowData.chainingParameter,
        // Chunk fields
        chunks: flowData.chunks,
        chunkCollectHours: flowData.chunkCollectHours,
        chunkPushHours: flowData.chunkPushHours,
        chunkTimeoutHours: flowData.chunkTimeoutHours,
        // Aggregation fields
        aggregateChunks: flowData.aggregateChunks,
        retainOriginalChunkFiles: flowData.retainOriginalChunkFiles,
        ignoreAggregationDupes: flowData.ignoreAggregationDupes,
        // Access keys (always empty when editing as they're not stored)
        accessKeyId: '',
        secretAccessKey: '',
        // Cron fields
        cronMin: flowData.cron?.[0] || '',
        cronHours: flowData.cron?.[1] || '',
        cronDayMonth: flowData.cron?.[2] || '',
        cronMonth: flowData.cron?.[3] || '',
        cronDayWeek: flowData.cron?.[4] || '',
        // Source filter fields (dynamic)
        ...sourceFilterFields,
        // Chain fields (dynamic)
        ...chainFields,
      });
      setSlugIsDirty(true);
      setEditMode(true);
    } else if (!flowSlug && !hasInitialized.current) {
      hasInitialized.current = true;
      // Initialize with default values for new flow (already set in useState)
      setFlowReadme(DEFAULT_README);
      setEditMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowSlug, selectedFlow?.id, form]);

  useEffect(() => {
    if (readmeContent) {
      setFlowReadme(readmeContent);
    }
  }, [readmeContent]);

  // Set up breadcrumbs
  useEffect(() => {
    const breadcrumbs: BreadcrumbItem[] = [
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Projects', href: `/orgs/${selectedOrgSlug}/projects` },
      {
        title: selectedProject?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/home`,
      },
      { title: 'Flows', href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/flows` },
    ];

    if (isEditMode && selectedFlow) {
      breadcrumbs.push(
        {
          title: selectedFlow.name || '',
          href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/flows/${selectedFlow.slug}`,
        },
        { title: 'Edit' },
      );
    } else {
      breadcrumbs.push({ title: 'New Flow' });
    }

    setBreadcrumbs(breadcrumbs);

    return () => {
      setBreadcrumbs([]);
    };
  }, [setBreadcrumbs, selectedOrganization, selectedOrgSlug, selectedProject, selectedProjectSlug, isEditMode, selectedFlow]);

  function handleSourceFilterChange(event: React.ChangeEvent<HTMLInputElement>, index: number) {
    const initialSourceCriteria: SourceCriteria = { filters: [{ parameters: {} }] };
    const updatedSourceCriteria = cloneDeep(values.sourceCriteria) || cloneDeep(initialSourceCriteria);

    if (event.target.value) {
      updatedSourceCriteria.filters[index].parameters[event.target.name] = event.target.value;
    } else {
      delete updatedSourceCriteria.filters[index].parameters[event.target.name];
    }

    handleFormDirtyCheck(values.sourceCriteria, updatedSourceCriteria);
    setValues((prev) => ({ ...prev, sourceCriteria: updatedSourceCriteria }));
  }

  const handleFieldChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { target } = event;
      const { checked, name } = target;

      if (name === 'aggregateChunks' && !checked) {
        event.stopPropagation();
        event.persist();
        setValues((prev) => ({
          ...prev,
          aggregateChunks: false,
          ignoreAggregationDupes: false,
          retainOriginalChunkFiles: false,
        }));
        // Sync with form fields
        form.setFieldsValue({
          aggregateChunks: false,
          ignoreAggregationDupes: false,
          retainOriginalChunkFiles: false,
        });
        setFormDirty(true);
      } else {
        handleChange(event);
      }
    },
    [handleChange, form],
  );

  const onFlowReadmeChange = (value: string) => {
    handleFormDirtyCheck(flowReadme, value);
    setFlowReadme(value);
  };

  const setupInputUriTemplateSections = (uriTemplate?: string): InputUriTemplate => {
    const regex = /^s3:\/\/([^/]+)\/(.*?([^/]+)\/?)$/i;
    const matchGroups = uriTemplate?.match(regex);

    const bucketRegex = /^s3:\/\/([^/]+)(\.+\w+)$/i;
    const matchBucket = uriTemplate?.match(bucketRegex);

    const extension = `.${uriTemplate?.split('.')?.pop()}`;

    if (matchGroups) {
      return {
        bucket: matchGroups[1],
        path: matchGroups[2].replace(extension, ''),
        extension: extension,
        fileName: '',
      };
    } else if (matchBucket) {
      return {
        bucket: matchBucket[1],
        path: '',
        extension: extension,
        fileName: '',
      };
    } else {
      return {
        bucket: '',
        path: '',
        extension: '.json',
        fileName: '',
      };
    }
  };

  const onBackClick = () => {
    if (isEditMode) {
      void history({
        to: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/flows/${selectedFlow.slug}`,
      });
    } else {
      void history({ to: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/flows` });
    }
  };

  async function saveFlow() {
    const formValues = form.getFieldsValue();
    const values = { ...currentValues, ...formValues };
    const flowType = values.type === 'CHAINED' ? 'SIMPLE' : values.type;

    try {
      if (isEditMode && selectedFlow) {
        const flowDefinition = flowType === 'SIMPLE' ? pickBy(setFlowDefinition(values), (v) => v !== undefined) : null;

        const cron = values.cronScheduling ? values.cron.join(' ') : null;

        const sourceCriteria = values.sourceFiltering ? cleanupSourceCriteria(values.sourceCriteria) : null;

        if (selectedFlow.definition.shuffleOnInputUriChange) {
          delete selectedFlow.definition.shuffleOnInputUriChange; // in case shuffleOnInputUriChange was saved on the definition
        }

        const updatedFlow = {
          ...(selectedFlow.name !== values.name && { name: values.name }),
          ...(selectedFlow.cron !== cron && { cron: cron }),
          ...(selectedFlow.pushHours !== values.pushHours && {
            pushHours: Number(values.pushHours),
          }),
          ...(selectedFlow.dataHours !== values.dataHours && {
            dataHours: Number(values.dataHours),
          }),
          ...(selectedFlow.closeHours !== values.closeHours && {
            closeHours: Number(values.closeHours),
          }),
          ...(selectedFlow.active !== values.active && { active: values.active }),
          ...(selectedFlow.skipQaTests !== values.skipQaTests && {
            skipQaTests: values.skipQaTests,
          }),
          ...(flowType === 'SIMPLE' && !isEqual(selectedFlow.definition, flowDefinition) && { definition: flowDefinition }),
          ...(selectedFlow.type !== 'LEGACY' &&
            (encryptedConfig.accessKeyId || encryptedConfig.secretAccessKey) && {
              encryptedConfig: encryptedConfig,
            }),
          ...(!isEqual(selectedFlow.sourceCriteria, sourceCriteria) && {
            sourceCriteria: sourceCriteria,
          }),
        };

        if (isWarningModalVisible) {
          toggleWarningModal();
        }

        // Update flow
        await updateFlowAsync(updatedFlow);

        // Save readme if it changed
        if (flowReadme !== readmeContent && flowId) {
          await saveFlowReadmeAsync({ flowId: flowId, content: flowReadme });
        }

        // Redirect to flow home page
        void history({
          to: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/flows/${selectedFlow.slug}`,
        });
      } else {
        const newFlow = {
          name: values.name,
          cron: values.cronScheduling ? values.cron.join(' ') : undefined,
          pushHours: Number(values.pushHours),
          dataHours: Number(values.dataHours),
          closeHours: Number(values.closeHours),
          active: values.active,
          type: flowType,
          definition: setFlowDefinition(values),
          slug: values.slug,
          encryptedConfig: encryptedConfig.accessKeyId || encryptedConfig.secretAccessKey ? encryptedConfig : undefined,
          sourceCriteria: values.sourceFiltering ? values.sourceCriteria : null,
          skipQaTests: values.skipQaTests,
        };

        // Create flow
        const createdFlow = await createFlowAsync(newFlow);

        // Add readme to the newly created flow
        if (flowReadme && createdFlow?.id) {
          await saveFlowReadmeAsync({ flowId: createdFlow.id, content: flowReadme });
        }

        // Redirect to flow home page
        if (createdFlow?.slug) {
          void history({
            to: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/flows/${createdFlow.slug}`,
          });
        }
      }
    } catch (error) {
      console.error('Error saving flow', error);
      // Error is already handled by the mutation hooks
    }
  }

  function toggleWarningModal() {
    setWarningModalVisible(!isWarningModalVisible);
  }

  const setFlowDefinition = (flow: FlowValues): any => {
    const s3Bucket = s3ConfiguredBucket?.replace('S3://', 's3://');
    if (flow.type === 'LEGACY') {
      return {
        collectionId: flow.collectionId,
      };
    } else if (flow.type === 'SIMPLE') {
      return {
        collectionId: flow.collectionId,
        inputUriTemplate: flow.s3Configuration ? s3Bucket : undefined,
        chunks: checkNumberValue(flow.chunks) ? Number(flow.chunks) : undefined,
        chunkCollectHours: checkNumberValue(flow.chunkCollectHours) ? Number(flow.chunkCollectHours) : undefined,
        chunkPushHours: checkNumberValue(flow.chunkPushHours) ? Number(flow.chunkPushHours) : undefined,
        chunkTimeoutHours: checkNumberValue(flow.chunkTimeoutHours) ? Number(flow.chunkTimeoutHours) : undefined,
        aggregateChunks: flow.aggregateChunks || undefined,
        retainOriginalChunkFiles: flow.retainOriginalChunkFiles || undefined,
        ignoreAggregationDupes: flow.ignoreAggregationDupes || undefined,
        inputTransform: flow.inputTransform || undefined,
      };
    } else if (flow.type === 'CHAINED') {
      return {
        collectionId: flow.chains[0].collectionId,
        inputUriTemplate:
          flow.s3Configuration || (!!flow.inputUriTemplate && !!Object.keys(flow.inputUriTemplate).length) ? s3Bucket : undefined,
        chains: flow.chains.map((chain) => ({
          ...chain,
          dataHours: checkNumberValue(chain.dataHours) ? Number(chain.dataHours) : undefined,
          chunkCollectHours: checkNumberValue(chain.chunkCollectHours) ? Number(chain.chunkCollectHours) : undefined,
          keyTransform: chain.keyTransform || undefined,
        })),
        chainingParameter: flow.chainingParameter,
        chunks: checkNumberValue(flow.chunks) ? Number(flow.chunks) : undefined,
        chunkCollectHours: checkNumberValue(flow.chunkCollectHours) ? Number(flow.chunkCollectHours) : undefined,
        chunkPushHours: checkNumberValue(flow.chunkPushHours) ? Number(flow.chunkPushHours) : undefined,
        chunkTimeoutHours: checkNumberValue(flow.chunkTimeoutHours) ? Number(flow.chunkTimeoutHours) : undefined,
        aggregateChunks: flow.aggregateChunks || undefined,
        retainOriginalChunkFiles: flow.retainOriginalChunkFiles || undefined,
        ignoreAggregationDupes: flow.ignoreAggregationDupes || undefined,
        inputTransform: flow.inputTransform || undefined,
      };
    }
  };

  const min = 0.1;
  const max = 999.99;

  // Simplified - let form validation handle it, just disable while saving
  const isSaveDisabled = isSavingFlow;

  const toggleActive = (e: any) => {
    const checked = e.target.checked;
    handleFormDirtyCheck(values.active, checked);
    setValues((prev) => ({ ...prev, active: checked }));
    form.setFieldsValue({ active: checked });
  };

  const resetCronValues = useCallback((cronVals: string[]) => {
    setValues((prev) => ({ ...prev, cron: cronVals }));
  }, []);

  const resetS3Values = useCallback(
    (s3Vals: any) => {
      setValues((prev) => ({
        ...prev,
        inputUriTemplate: s3Vals.inputUriTemplate,
        chunks: s3Vals.chunks,
        chunkCollectHours: s3Vals.chunkCollectHours,
        chunkPushHours: s3Vals.chunkPushHours,
        chunkTimeoutHours: s3Vals.chunkTimeoutHours,
        aggregateChunks: false,
        retainOriginalChunkFiles: false,
        ignoreAggregationDupes: false,
        inputTransform: undefined,
      }));

      // Reset form fields
      form.setFieldsValue({
        bucket: s3Vals.inputUriTemplate?.bucket ?? '',
        path: s3Vals.inputUriTemplate?.path ?? '',
        fileName: s3Vals.inputUriTemplate?.fileName ?? '',
        extension: s3Vals.inputUriTemplate?.extension ?? '.json',
        chunks: s3Vals.chunks,
        chunkCollectHours: s3Vals.chunkCollectHours,
        chunkPushHours: s3Vals.chunkPushHours,
        chunkTimeoutHours: s3Vals.chunkTimeoutHours,
        aggregateChunks: false,
        retainOriginalChunkFiles: false,
        ignoreAggregationDupes: false,
        inputTransform: undefined,
        accessKeyId: s3Vals.encryptedConfig?.accessKeyId ?? '',
        secretAccessKey: s3Vals.encryptedConfig?.secretAccessKey ?? '',
      });

      setEncryptedConfig(s3Vals.encryptedConfig);
    },
    [form],
  );

  // Use state values for rendering - no need for derived values since they're already in state
  const currentFlowType = values.type || 'SIMPLE';
  const currentValues = values;

  let formType: ReactElement | null;

  switch (currentFlowType) {
    case 'LEGACY':
      formType = (
        <LegacyExtractorFlow
          collections={collections}
          flow={currentValues}
          handleFieldChange={handleChange}
          isEditMode={isEditMode}
          orgSlug={selectedOrgSlug!}
          projectSlug={selectedProjectSlug!}
        />
      );
      break;
    case 'SIMPLE':
      formType = (
        <SimpleExtractorFlow
          collections={collections}
          flow={currentValues}
          handleFieldChange={handleFieldChange}
          isEditMode={isEditMode}
          s3ConfiguredBucket={s3ConfiguredBucket}
          handleS3Change={handleS3Change}
          handleSourceFilterChange={(e, index) => handleSourceFilterChange(e, index)}
          resetS3Values={resetS3Values}
          isFormDirty={isFormDirty}
          orgSlug={selectedOrgSlug!}
          projectSlug={selectedProjectSlug!}
          chained={false}
        />
      );
      break;
    case 'CHAINED':
      formType = (
        <SimpleExtractorFlow
          collections={collections}
          flow={currentValues}
          handleFieldChange={handleFieldChange}
          handleChainCancel={(e, idx) => handleChainCancel(e, idx)}
          handleChainChange={(e, idx) => handleChainChange(e, idx)}
          addChainToFlowArray={addChainToFlowArray}
          isEditMode={isEditMode}
          invalidChainArray={invalidChainArray}
          s3ConfiguredBucket={s3ConfiguredBucket}
          handleS3Change={handleS3Change}
          handleSourceFilterChange={(e, index) => handleSourceFilterChange(e, index)}
          resetS3Values={resetS3Values}
          isFormDirty={isFormDirty}
          orgSlug={selectedOrgSlug!}
          projectSlug={selectedProjectSlug!}
          chained
        />
      );
      break;
    default:
      formType = null;
      break;
  }

  return (
    <>
      <PageTitle title={isEditMode ? 'Edit Flow' : 'New Flow'} />
      <div style={{ maxWidth: '100%' }}>
        {/* Form */}
        <Form
          form={form}
          onFinish={saveFlow}
          layout="vertical"
          initialValues={{
            name: '',
            slug: '',
            type: 'SIMPLE',
            active: true,
            collectionId: '',
            cronScheduling: false,
            s3Configuration: false,
            skipQaTests: false,
            sourceFiltering: false,
            // S3 fields
            bucket: '',
            path: '',
            fileName: '',
            extension: '.json',
            // Transform fields
            inputTransform: '',
            chainingParameter: '',
            // Chunk fields
            chunks: undefined,
            chunkCollectHours: undefined,
            chunkPushHours: undefined,
            chunkTimeoutHours: undefined,
            // Aggregation fields
            aggregateChunks: false,
            retainOriginalChunkFiles: false,
            ignoreAggregationDupes: false,
            // Access keys
            accessKeyId: '',
            secretAccessKey: '',
            // Cron fields
            cronMin: '',
            cronHours: '',
            cronDayMonth: '',
            cronMonth: '',
            cronDayWeek: '',
          }}
          onValuesChange={(changedValues, allValues) => {
            // Auto-generate slug from name if slug hasn't been manually edited
            if (changedValues.name && !slugIsDirty) {
              const suggestedSlug = createSuggestedSlug(changedValues.name);
              form.setFieldsValue({ slug: suggestedSlug });
              setValues((prev) => ({ ...prev, slug: suggestedSlug }));
            }

            // Validate slug
            if (changedValues.slug !== undefined) {
              const slugValue = changedValues.slug;
              setValidSlug(validateSlug(slugValue));
              setSlugIsDirty(true);
              const duplicateSlug = checkIfSlugExists(flows, slugValue);
              if (duplicateSlug) {
                setFlowSlugErrors([`A flow with the slug "${slugValue}" already exists`]);
              } else {
                setFlowSlugErrors([]);
              }
            }

            // Validate name
            if (changedValues.name !== undefined) {
              const nameValue = changedValues.name;
              if (isEditMode && selectedFlow && nameValue.toLowerCase() === selectedFlow.name.toLowerCase()) {
                setFlowNameErrors([]);
              } else {
                const duplicateName = checkIfNameExists(flows, nameValue);
                if (duplicateName) {
                  setFlowNameErrors([`A flow with the name "${nameValue}" already exists`]);
                } else {
                  setFlowNameErrors([]);
                }
              }
            }

            // Handle access keys - sync with encryptedConfig state
            if ('accessKeyId' in changedValues || 'secretAccessKey' in changedValues) {
              setEncryptedConfig((prev) => ({
                ...prev,
                accessKeyId: allValues.accessKeyId ?? prev.accessKeyId ?? '',
                secretAccessKey: allValues.secretAccessKey ?? prev.secretAccessKey ?? '',
              }));
            }

            // Update manual state with changed form values only
            // Map flat form fields to nested structure
            setValues((prev) => {
              const updatedValues: any = { ...prev };

              // Handle S3 fields - only update if they changed
              if (
                'bucket' in changedValues ||
                'path' in changedValues ||
                'fileName' in changedValues ||
                'extension' in changedValues
              ) {
                updatedValues.inputUriTemplate = {
                  ...updatedValues.inputUriTemplate,
                  ...(changedValues.bucket !== undefined && { bucket: changedValues.bucket }),
                  ...(changedValues.path !== undefined && { path: changedValues.path }),
                  ...(changedValues.fileName !== undefined && { fileName: changedValues.fileName }),
                  ...(changedValues.extension !== undefined && {
                    extension: changedValues.extension,
                  }),
                };
              }

              // Handle cron fields - sync with cron array
              const cronFieldMap = {
                cronMin: 0,
                cronHours: 1,
                cronDayMonth: 2,
                cronMonth: 3,
                cronDayWeek: 4,
              };
              const cronFields = Object.keys(cronFieldMap);
              const changedCronFields = cronFields.filter((field) => field in changedValues);
              if (changedCronFields.length > 0) {
                const newCron = [...prev.cron];
                changedCronFields.forEach((field) => {
                  newCron[cronFieldMap[field]] = changedValues[field];
                });
                updatedValues.cron = newCron;
              }

              // Handle other fields - only update changed fields (exclude access keys, S3, and cron fields)
              const excludedFields = ['bucket', 'path', 'fileName', 'extension', 'accessKeyId', 'secretAccessKey', ...cronFields];
              Object.keys(changedValues).forEach((key) => {
                if (!excludedFields.includes(key)) {
                  updatedValues[key] = changedValues[key];
                }
              });

              return updatedValues;
            });
            setFormDirty(true);
          }}
        >
          {flowApiErrors.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              {flowApiErrors.map((error) => (
                <Alert key={error} title={error} type="error" showIcon />
              ))}
            </div>
          )}
          <Form.Item
            label="Name"
            name="name"
            required
            validateStatus={flowNameErrors.length ? 'error' : ''}
            rules={[{ required: true, message: 'Please input flow name!' }]}
          >
            <Input id="name" aria-describedby="project-flow-name" />
          </Form.Item>
          {flowNameErrors.map((error) => (
            <ErrorField key={error} errorText={error} />
          ))}
          <Form.Item
            label="Slug / ID - CANNOT BE CHANGED AFTER CREATION"
            name="slug"
            required
            validateStatus={!isValidSlug ? 'error' : ''}
            rules={[{ required: true, message: 'Please input slug!' }]}
          >
            <Input id="flow-slug" aria-describedby="project-flow-slug" disabled={!!isEditMode} />
          </Form.Item>
          {!isValidSlug && <SlugField />}
          {flowSlugErrors.map((error) => (
            <ErrorField key={error} errorText={error} />
          ))}
          <Form.Item label="Type" name="type" required rules={[{ required: true, message: 'Please select type!' }]}>
            <Select onChange={handleTypeChange} disabled={isEditMode} id="type" options={flowTypeOptions} />
          </Form.Item>
          <Form.Item label="Active" name="active" valuePropName="checked">
            <Checkbox onChange={toggleActive} data-test-id="new-flow-active-checkbox">
              Active
            </Checkbox>
          </Form.Item>
          {formType}
          <Divider style={{ margin: '20px 0' }} />
          <FlowCronExpression
            flow={currentValues}
            handleCronChange={handleCronChange}
            handleFieldChange={handleChange}
            isValidCronExpression={_isValidCronExpression}
            resetCronValues={resetCronValues}
          />
          <Divider style={{ margin: '20px 0' }} />
          <div data-test-id="flow-collection-info">
            <div className={classes.sectionTitle}>Collection Information:</div>
            <Space align="start" size="middle">
              <Form.Item
                label="Hours to Collect Data"
                name="dataHours"
                className={classes.horItem}
                rules={[
                  { required: true, message: 'Please input hours to collect data!' },
                  {
                    validator: (_, value) => {
                      if (value && !checkPositiveNumberWithMinMaxValue(value, min, max)) {
                        return Promise.reject(new Error(`Value must be between ${min} and ${max}`));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  id="dataHours"
                  aria-describedby="project-flow-data-hours"
                  className={classes.horInput}
                  min={min}
                  max={max}
                  step={0.01}
                />
              </Form.Item>
              <Form.Item
                label="Hours from Start to Destination Push"
                name="pushHours"
                className={classes.horItem}
                rules={[
                  { required: true, message: 'Please input hours from start to destination push!' },
                  {
                    validator: (_, value) => {
                      if (value && !checkPositiveNumberWithMinMaxValue(value, min, max)) {
                        return Promise.reject(new Error(`Value must be between ${min} and ${max}`));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  id="pushHours"
                  aria-describedby="project-flow-push=hours"
                  className={classes.horInput}
                  min={min}
                  max={max}
                  step={0.01}
                />
              </Form.Item>
              <Form.Item
                label="Hours to Finish"
                name="closeHours"
                className={classes.horItem}
                rules={[
                  { required: true, message: 'Please input hours to finish!' },
                  {
                    validator: (_, value) => {
                      if (value && !checkPositiveNumberWithMinMaxValue(value, min, max)) {
                        return Promise.reject(new Error(`Value must be between ${min} and ${max}`));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  id="closeHours"
                  aria-describedby="project-flow-close-hours"
                  className={classes.horInput}
                  min={min}
                  max={max}
                  step={0.01}
                />
              </Form.Item>
            </Space>
          </div>
          {/* read me editor */}
          <div
            style={{
              marginTop: 25,
              marginBottom: 25,
              ...(access ? {} : { pointerEvents: 'none', opacity: 0.7 }),
            }}
          >
            <MarkdownEditor editorOptions={README_EDITOR_OPTIONS} onChange={onFlowReadmeChange} value={flowReadme} />
            {!flowReadme && <RequiredField />}
          </div>
          <ConfirmButtons confirmText="Save" disabled={isSaveDisabled} onCancel={onBackClick} onConfirm={handleSubmit} />
        </Form>

        <Modal open={isWarningModalVisible} title="Save Flow Warning" onCancel={toggleWarningModal} onOk={saveFlow}>
          <p>Are you sure you want to save without any source filters?</p>
        </Modal>
      </div>
    </>
  );
}

export default NewProjectFlow;
